import { computed, ref, shallowRef } from 'vue';
import { apiRequest, assertOk } from '@/api/http';
import { SchoolLevel } from '@/constants/accounts';
import { JHS_GRADES, SHS_GRADES } from '@/constants/risk';

const titleCase = (s) =>
  String(s ?? '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// A subject's level ("Junior High" / "Senior High") and, for Senior High,
// its grade: what the list shows under the title.
const subjectMeta = (s) => s.senior_level || s.grade_level || '';
// The subjects' grade_level for each account school level.
const SUBJECT_LEVEL = { [SchoolLevel.Junior]: 'Junior High', [SchoolLevel.Senior]: 'Senior High' };

// The two lists a teacher keeps on their profile. Rows already saved carry
// their link id (listKey); the backend adds rows without one and deletes the
// link ids sent in the "removed" list.
const KINDS = {
  subjects: {
    listKey: 'subjectListId',
    // Only the subjects of the account's school level; BOTH or no level
    // offers them all.
    async options(level) {
      const rows = (await apiRequest('get', '/subjects/getAllSubject/active')) || [];
      const only = SUBJECT_LEVEL[level];
      return rows
        .filter((s) => !only || s.grade_level === only)
        .map((s) => ({ id: Number(s.id), title: titleCase(s.subject_title), meta: subjectMeta(s) }));
    },
    async tagged(teacherId) {
      const rows = (await apiRequest('get', `/subjects/getSubjectTaagged/${teacherId}`)) || [];
      return rows.map((r) => ({
        id: Number(r.subjectId),
        linkId: r.subjectListId,
        title: titleCase(r.subject_title),
        meta: subjectMeta(r),
      }));
    },
    save: (teacherId, list, removed) =>
      apiRequest('post', '/subjects/addTeachersSubject', {
        userID: teacherId,
        subject_list: JSON.stringify(list),
        removed_subjects: JSON.stringify(removed),
      }),
  },
  grades: {
    listKey: 'gradeListId',
    // Only the grades of the account's school level: JHS 7-10, SHS 11-12.
    async options(level) {
      const grades =
        level === SchoolLevel.Junior
          ? JHS_GRADES
          : level === SchoolLevel.Senior
            ? SHS_GRADES
            : [...JHS_GRADES, ...SHS_GRADES];
      return grades.map((n) => ({ id: n, title: `Grade ${n}`, meta: '' }));
    },
    async tagged(teacherId) {
      const rows = (await apiRequest('get', `/subjects/getGradeTaagged/${teacherId}`)) || [];
      return rows.map((r) => ({
        id: Number(r.grade_level),
        linkId: r.gradeListId,
        title: `Grade ${Number(r.grade_level)}`,
        meta: '',
      }));
    },
    save: (teacherId, list, removed) =>
      apiRequest('post', '/subjects/addTeachers/Grade-Level', {
        userID: teacherId,
        gradeLevel_list: JSON.stringify(list),
        removed_gradeLevel: JSON.stringify(removed),
      }),
  },
};

// kind: 'subjects' | 'grades'. teacherId: the user's user-detail id; level:
// their account's school level (JHS/SHS/BOTH), which narrows the options.
export function useTeachingList(kind) {
  const api = KINDS[kind];
  const options = shallowRef([]);
  const saved = shallowRef([]);
  const items = ref([]);
  const loading = shallowRef(true);
  const saving = shallowRef(false);
  const savedAt = shallowRef(0);
  let teacherId = null;
  let level = null;

  const ids = computed(() => new Set(items.value.map((i) => i.id)));
  const available = computed(() => options.value.filter((o) => !ids.value.has(o.id)));
  const isDirty = computed(() => {
    const before = new Set(saved.value.map((i) => i.id));
    return before.size !== ids.value.size || [...before].some((id) => !ids.value.has(id));
  });

  async function load(id, accountLevel = level) {
    teacherId = id;
    level = accountLevel;
    loading.value = true;
    try {
      const [opts, tagged] = await Promise.all([api.options(level), api.tagged(id)]);
      options.value = opts;
      saved.value = tagged;
      items.value = [...tagged];
    } finally {
      loading.value = false;
    }
  }

  function add(id) {
    const option = options.value.find((o) => o.id === id);
    if (option && !ids.value.has(id)) items.value = [...items.value, { ...option }];
  }
  function remove(item) {
    items.value = items.value.filter((i) => i !== item);
  }
  function reset() {
    items.value = [...saved.value];
  }

  async function save() {
    saving.value = true;
    try {
      // A saved row removed and added back again keeps its link.
      const savedById = new Map(saved.value.map((i) => [i.id, i]));
      const list = items.value.map((i) => ({
        id: i.id,
        [api.listKey]: i.linkId ?? savedById.get(i.id)?.linkId,
      }));
      const removed = saved.value
        .filter((i) => !ids.value.has(i.id))
        .map((i) => ({ [api.listKey]: i.linkId }));
      assertOk(await api.save(teacherId, list, removed));
      await load(teacherId);
      savedAt.value = Date.now();
    } finally {
      saving.value = false;
    }
  }

  return { items, available, loading, saving, savedAt, isDirty, load, add, remove, reset, save };
}
